import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus, Trash2, Calendar as CalendarIcon } from 'lucide-react';
import { materialManagementAPI, masterDataAPI, companyAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const MaterialManagement = () => {
  const { canWrite } = useAuth();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'holiday-calendars';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  
  const [data, setData] = useState({
    holidayCalendars: [],
    factoryCalendars: [],
    plants: [],
    storageLocations: [],
    purchasingOrgs: [],
    purchasingGroups: [],
  });

  // Foreign key data
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [formResetCounter, setFormResetCounter] = useState(0);

  // State for form selections
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedCompanyCode, setSelectedCompanyCode] = useState('');
  const [selectedFactoryCalendar, setSelectedFactoryCalendar] = useState('');
  const [selectedHolidayCalendar, setSelectedHolidayCalendar] = useState('');
  const [selectedWorkdays, setSelectedWorkdays] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [selectedType, setSelectedType] = useState('');

  // Holiday and special rule management
  const [holidays, setHolidays] = useState([]);
  const [specialRules, setSpecialRules] = useState([]);

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    loadData();
    loadForeignKeyData();
  }, []);

  // Reset ALL form state whenever tab changes
  useEffect(() => {
    setEditingItem(null);
    setIsDialogOpen(false);
    setHolidays([]);
    setSpecialRules([]);
    setSelectedWorkdays([]);
    setSelectedType('');
    setSelectedPlant('');
    setSelectedStatus('Active');
    setSelectedCountry('');
    setSelectedCurrency('');
    setSelectedLanguage('');
    setSelectedCompanyCode('');
    setSelectedFactoryCalendar('');
    setSelectedHolidayCalendar('');
    setFormResetCounter(prev => prev + 1); // Force form remount
  }, [activeTab]);

  const loadForeignKeyData = async () => {
    try {
      const [countryRes, currRes, langRes, ccRes] = await Promise.all([
        masterDataAPI.getCountries(),
        masterDataAPI.getCurrencies(),
        masterDataAPI.getLanguages(),
        companyAPI.getCompanyCodes(),
      ]);
      setCountries(countryRes.data);
      setCurrencies(currRes.data);
      setLanguages(langRes.data);
      setCompanyCodes(ccRes.data);
    } catch (error) {
      console.error('Failed to load foreign key data:', error);
    }
  };

  const loadData = async () => {
    try {
      const [hc, fc, pl, sl, po, pg] = await Promise.all([
        materialManagementAPI.getHolidayCalendars(),
        materialManagementAPI.getFactoryCalendars(),
        materialManagementAPI.getPlants(),
        materialManagementAPI.getStorageLocations(),
        materialManagementAPI.getPurchasingOrgs(),
        materialManagementAPI.getPurchasingGroups(),
      ]);
      
      setData({
        holidayCalendars: hc.data,
        factoryCalendars: fc.data,
        plants: pl.data,
        storageLocations: sl.data,
        purchasingOrgs: po.data,
        purchasingGroups: pg.data,
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const formValues = Object.fromEntries(formData.entries());

    // Convert empty strings to null
    Object.keys(formValues).forEach(key => {
      if (formValues[key] === '') {
        formValues[key] = null;
      }
    });

    try {
      if (activeTab === 'holiday-calendars') {
        const payload = {
          holiday_calendar_id: formValues.holiday_calendar_id,
          description: formValues.description,
          country: formValues.country,
          region: formValues.region || null,
          valid_from: parseInt(formValues.valid_from),
          valid_to: parseInt(formValues.valid_to),
          holidays: holidays
        };

        if (editingItem) {
          await materialManagementAPI.updateHolidayCalendar(editingItem.holiday_calendar_id, payload);
          toast.success('Holiday Calendar updated successfully');
        } else {
          await materialManagementAPI.createHolidayCalendar(payload);
          toast.success('Holiday Calendar created successfully');
        }
      } else if (activeTab === 'factory-calendars') {
        const payload = {
          factory_calendar_id: formValues.factory_calendar_id,
          description: formValues.description,
          holiday_calendar_id: formValues.holiday_calendar_id,
          valid_from: parseInt(formValues.valid_from),
          valid_to: parseInt(formValues.valid_to),
          workdays: selectedWorkdays,
          special_rules: specialRules
        };

        if (editingItem) {
          await materialManagementAPI.updateFactoryCalendar(editingItem.factory_calendar_id, payload);
          toast.success('Factory Calendar updated successfully');
        } else {
          await materialManagementAPI.createFactoryCalendar(payload);
          toast.success('Factory Calendar created successfully');
        }
      } else if (activeTab === 'plants') {
        // Validate required dropdown selections
        if (!formValues.company_code_id || !formValues.country || !formValues.language || 
            !formValues.currency || !formValues.factory_calendar_id) {
          toast.error('Please select all required dropdown fields');
          return;
        }
        
        const payload = {
          plant_id: formValues.plant_id,
          plant_name: formValues.plant_name,
          company_code_id: formValues.company_code_id,
          city: formValues.city,
          country: formValues.country,
          region: formValues.region || null,
          address_street: formValues.address_street || null,
          address_po_box: formValues.address_po_box || null,
          postal_code: formValues.postal_code || null,
          language: formValues.language,
          currency: formValues.currency,
          factory_calendar_id: formValues.factory_calendar_id,
          time_zone: formValues.time_zone || null,
        };

        if (editingItem) {
          await materialManagementAPI.updatePlant(editingItem.plant_id, payload);
          toast.success('Plant updated successfully');
        } else {
          await materialManagementAPI.createPlant(payload);
          toast.success('Plant created successfully');
        }
      } else if (activeTab === 'storage-locations') {
        const payload = {
          storage_location_id: formValues.storage_location_id,
          plant_id: formValues.plant_id,
          name: formValues.name,
          description: formValues.description || null,
          address: formValues.address || null,
          status: formValues.status,
          type: formValues.type || null,
          capacity: formValues.capacity ? parseInt(formValues.capacity) : null,
          zone: formValues.zone || null,
          manager_id: formValues.manager_id || null,
        };

        if (editingItem) {
          await materialManagementAPI.updateStorageLocation(editingItem.plant_id, editingItem.storage_location_id, payload);
          toast.success('Storage Location updated successfully');
        } else {
          await materialManagementAPI.createStorageLocation(payload);
          toast.success('Storage Location created successfully');
        }
      } else if (activeTab === 'purchasing-organizations') {
        const payload = {
          purchasing_org_id: formValues.purchasing_org_id,
          name: formValues.name,
          description: formValues.description || null,
          company_code_id: formValues.company_code_id || null,
          address: formValues.address || null,
          language: formValues.language || null,
          currency: formValues.currency || null,
          status: formValues.status,
        };

        if (editingItem) {
          await materialManagementAPI.updatePurchasingOrg(editingItem.purchasing_org_id, payload);
          toast.success('Purchasing Organization updated successfully');
        } else {
          await materialManagementAPI.createPurchasingOrg(payload);
          toast.success('Purchasing Organization created successfully');
        }
      } else if (activeTab === 'purchasing-groups') {
        const payload = {
          purchasing_group_id: formValues.purchasing_group_id,
          name: formValues.name,
          description: formValues.description || null,
          email: formValues.email || null,
          phone: formValues.phone || null,
          manager_id: formValues.manager_id || null,
          status: formValues.status,
        };

        if (editingItem) {
          await materialManagementAPI.updatePurchasingGroup(editingItem.purchasing_group_id, payload);
          toast.success('Purchasing Group updated successfully');
        } else {
          await materialManagementAPI.createPurchasingGroup(payload);
          toast.success('Purchasing Group created successfully');
        }
      }

      setIsDialogOpen(false);
      setEditingItem(null);
      setHolidays([]);
      setSpecialRules([]);
      setSelectedWorkdays([]);
      setSelectedType('');
      setSelectedPlant('');
      setSelectedStatus('Active');
      setSelectedCountry('');
      setSelectedCurrency('');
      setSelectedLanguage('');
      setSelectedCompanyCode('');
      setSelectedFactoryCalendar('');
      setSelectedHolidayCalendar('');
      setFormResetCounter(prev => prev + 1); // Force form remount
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    
    if (activeTab === 'holiday-calendars') {
      setSelectedCountry(item.country);
      setHolidays(item.holidays || []);
    } else if (activeTab === 'factory-calendars') {
      setSelectedHolidayCalendar(item.holiday_calendar_id);
      setSelectedWorkdays(item.workdays || []);
      setSpecialRules(item.special_rules || []);
    } else if (activeTab === 'plants') {
      setSelectedCountry(item.country);
      setSelectedCurrency(item.currency);
      setSelectedLanguage(item.language);
      setSelectedCompanyCode(item.company_code_id);
      setSelectedFactoryCalendar(item.factory_calendar_id);
    } else if (activeTab === 'storage-locations') {
      setSelectedPlant(item.plant_id);
      setSelectedStatus(item.status);
      setSelectedType(item.type || '');
    } else if (activeTab === 'purchasing-organizations') {
      setSelectedCompanyCode(item.company_code_id || '');
      setSelectedCurrency(item.currency || '');
      setSelectedLanguage(item.language || '');
      setSelectedStatus(item.status);
    } else if (activeTab === 'purchasing-groups') {
      setSelectedStatus(item.status);
    }
    
    setIsDialogOpen(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      if (activeTab === 'holiday-calendars') {
        await materialManagementAPI.deleteHolidayCalendar(item.holiday_calendar_id);
      } else if (activeTab === 'factory-calendars') {
        await materialManagementAPI.deleteFactoryCalendar(item.factory_calendar_id);
      } else if (activeTab === 'plants') {
        await materialManagementAPI.deletePlant(item.plant_id);
      } else if (activeTab === 'storage-locations') {
        await materialManagementAPI.deleteStorageLocation(item.plant_id, item.storage_location_id);
      } else if (activeTab === 'purchasing-organizations') {
        await materialManagementAPI.deletePurchasingOrg(item.purchasing_org_id);
      } else if (activeTab === 'purchasing-groups') {
        await materialManagementAPI.deletePurchasingGroup(item.purchasing_group_id);
      }

      toast.success('Item deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const addHoliday = () => {
    const newHoliday = {
      name: '',
      type: 'public'
    };
    setHolidays([...holidays, newHoliday]);
  };

  const removeHoliday = (index) => {
    setHolidays(holidays.filter((_, i) => i !== index));
  };

  const updateHoliday = (index, field, value) => {
    const updated = [...holidays];
    updated[index][field] = value;
    setHolidays(updated);
  };

  const addSpecialRule = () => {
    const newRule = {
      date: '',
      type: 'additional_holiday',
      description: ''
    };
    setSpecialRules([...specialRules, newRule]);
  };

  const removeSpecialRule = (index) => {
    setSpecialRules(specialRules.filter((_, i) => i !== index));
  };

  const updateSpecialRule = (index, field, value) => {
    const updated = [...specialRules];
    updated[index][field] = value;
    setSpecialRules(updated);
  };

  const toggleWorkday = (day) => {
    if (selectedWorkdays.includes(day)) {
      setSelectedWorkdays(selectedWorkdays.filter(d => d !== day));
    } else {
      setSelectedWorkdays([...selectedWorkdays, day]);
    }
  };

  const getFormFields = () => {
    switch (activeTab) {
      case 'holiday-calendars':
        return (
          <>
            <div>
              <Label>Holiday Calendar ID</Label>
              <Input 
                name="holiday_calendar_id"
                defaultValue={editingItem?.holiday_calendar_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., IN01"
                maxLength={10}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                name="description"
                defaultValue={editingItem?.description}
                required
                placeholder="e.g., India Holidays 2025-2029"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Select value={selectedCountry} onValueChange={setSelectedCountry} required>
                <SelectTrigger><SelectValue placeholder="Select Country" /></SelectTrigger>
                <SelectContent>
                  {countries.map(c => (
                    <SelectItem key={c.country_code} value={c.country_code}>
                      {c.country_code} - {c.country_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="country" value={selectedCountry} />
            </div>
            <div>
              <Label>Region (optional)</Label>
              <Input name="region" defaultValue={editingItem?.region} placeholder="e.g., KA" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valid From (Year)</Label>
                <Input 
                  name="valid_from"
                  type="number"
                  defaultValue={editingItem?.valid_from || 2025}
                  required
                  min="2000"
                  max="2100"
                />
              </div>
              <div>
                <Label>Valid To (Year)</Label>
                <Input 
                  name="valid_to"
                  type="number"
                  defaultValue={editingItem?.valid_to || 2029}
                  required
                  min="2000"
                  max="2100"
                />
              </div>
            </div>
            
            {/* Holidays Section */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-base font-semibold">Holidays</Label>
                <Button type="button" size="sm" onClick={addHoliday}>
                  <Plus className="w-4 h-4 mr-1" /> Add Holiday
                </Button>
              </div>
              
              {holidays.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No holidays added yet</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {holidays.map((holiday, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded">
                      <div className="flex-1">
                        <Input 
                          placeholder="Holiday Name"
                          value={holiday.name}
                          onChange={(e) => updateHoliday(index, 'name', e.target.value)}
                        />
                      </div>
                      <div className="w-32">
                        <select
                          className="w-full px-3 py-2 border rounded"
                          value={holiday.type}
                          onChange={(e) => updateHoliday(index, 'type', e.target.value)}
                        >
                          <option value="public">Public</option>
                          <option value="regional">Regional</option>
                          <option value="religious">Religious</option>
                        </select>
                      </div>
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="destructive"
                        onClick={() => removeHoliday(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        );

      case 'factory-calendars':
        return (
          <>
            <div>
              <Label>Factory Calendar ID</Label>
              <Input 
                name="factory_calendar_id"
                defaultValue={editingItem?.factory_calendar_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., IN"
                maxLength={10}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                name="description"
                defaultValue={editingItem?.description}
                required
                placeholder="e.g., India Factory Calendar"
              />
            </div>
            <div>
              <Label>Holiday Calendar</Label>
              <Select value={selectedHolidayCalendar} onValueChange={setSelectedHolidayCalendar} required>
                <SelectTrigger><SelectValue placeholder="Select Holiday Calendar" /></SelectTrigger>
                <SelectContent>
                  {data.holidayCalendars.map(hc => (
                    <SelectItem key={hc.holiday_calendar_id} value={hc.holiday_calendar_id}>
                      {hc.holiday_calendar_id} - {hc.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="holiday_calendar_id" value={selectedHolidayCalendar} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valid From (Year)</Label>
                <Input 
                  name="valid_from"
                  type="number"
                  defaultValue={editingItem?.valid_from || 2025}
                  required
                  min="2000"
                  max="2100"
                />
              </div>
              <div>
                <Label>Valid To (Year)</Label>
                <Input 
                  name="valid_to"
                  type="number"
                  defaultValue={editingItem?.valid_to || 2029}
                  required
                  min="2000"
                  max="2100"
                />
              </div>
            </div>
            
            {/* Workdays Section */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">Working Days</Label>
              <div className="grid grid-cols-2 gap-2">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                  <label key={day} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={selectedWorkdays.includes(day)}
                      onChange={() => toggleWorkday(day)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{day}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Special Rules Section */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-base font-semibold">Special Rules (optional)</Label>
                <Button type="button" size="sm" onClick={addSpecialRule}>
                  <Plus className="w-4 h-4 mr-1" /> Add Rule
                </Button>
              </div>
              
              {specialRules.length > 0 && (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {specialRules.map((rule, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded">
                      <div className="flex-1">
                        <Input 
                          placeholder="Date (YYYY-MM-DD)"
                          value={rule.date}
                          onChange={(e) => updateSpecialRule(index, 'date', e.target.value)}
                        />
                      </div>
                      <div className="w-48">
                        <select
                          className="w-full px-3 py-2 border rounded"
                          value={rule.type}
                          onChange={(e) => updateSpecialRule(index, 'type', e.target.value)}
                        >
                          <option value="additional_holiday">Additional Holiday</option>
                          <option value="extra_working_day">Extra Working Day</option>
                          <option value="public_holiday_override">Holiday Override</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <Input 
                          placeholder="Description"
                          value={rule.description}
                          onChange={(e) => updateSpecialRule(index, 'description', e.target.value)}
                        />
                      </div>
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="destructive"
                        onClick={() => removeSpecialRule(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        );

      case 'plants':
        return (
          <>
            <div>
              <Label>Plant ID</Label>
              <Input 
                name="plant_id"
                defaultValue={editingItem?.plant_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., 1000"
                maxLength={10}
              />
            </div>
            <div>
              <Label>Plant Name</Label>
              <Input 
                name="plant_name"
                defaultValue={editingItem?.plant_name}
                required
                placeholder="e.g., Bangalore Main Plant"
              />
            </div>
            <div>
              <Label>Company Code</Label>
              <Select value={selectedCompanyCode} onValueChange={setSelectedCompanyCode} required>
                <SelectTrigger><SelectValue placeholder="Select Company Code" /></SelectTrigger>
                <SelectContent>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="company_code_id" value={selectedCompanyCode} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input name="city" defaultValue={editingItem?.city} required placeholder="e.g., Bangalore" />
              </div>
              <div>
                <Label>Country</Label>
                <Select value={selectedCountry} onValueChange={setSelectedCountry} required>
                  <SelectTrigger><SelectValue placeholder="Select Country" /></SelectTrigger>
                  <SelectContent>
                    {countries.map(c => (
                      <SelectItem key={c.country_code} value={c.country_code}>
                        {c.country_code} - {c.country_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" name="country" value={selectedCountry} />
              </div>
            </div>
            <div>
              <Label>Region (optional)</Label>
              <Input name="region" defaultValue={editingItem?.region} placeholder="e.g., Karnataka" />
            </div>
            <div>
              <Label>Street Address (optional)</Label>
              <Input name="address_street" defaultValue={editingItem?.address_street} placeholder="e.g., 1234 Industrial Ave" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PO Box (optional)</Label>
                <Input name="address_po_box" defaultValue={editingItem?.address_po_box} placeholder="PO Box" />
              </div>
              <div>
                <Label>Postal Code (optional)</Label>
                <Input name="postal_code" defaultValue={editingItem?.postal_code} placeholder="e.g., 560048" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Language</Label>
                <Select value={selectedLanguage} onValueChange={setSelectedLanguage} required>
                  <SelectTrigger><SelectValue placeholder="Select Language" /></SelectTrigger>
                  <SelectContent>
                    {languages.map(l => (
                      <SelectItem key={l.language_code} value={l.language_code}>
                        {l.language_code} - {l.language_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" name="language" value={selectedLanguage} />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={selectedCurrency} onValueChange={setSelectedCurrency} required>
                  <SelectTrigger><SelectValue placeholder="Select Currency" /></SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => (
                      <SelectItem key={c.currency_code} value={c.currency_code}>
                        {c.currency_code} - {c.currency_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" name="currency" value={selectedCurrency} />
              </div>
            </div>
            <div>
              <Label>Factory Calendar</Label>
              <Select value={selectedFactoryCalendar} onValueChange={setSelectedFactoryCalendar} required>
                <SelectTrigger><SelectValue placeholder="Select Factory Calendar" /></SelectTrigger>
                <SelectContent>
                  {data.factoryCalendars.map(fc => (
                    <SelectItem key={fc.factory_calendar_id} value={fc.factory_calendar_id}>
                      {fc.factory_calendar_id} - {fc.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="factory_calendar_id" value={selectedFactoryCalendar} />
            </div>
            <div>
              <Label>Time Zone (optional)</Label>
              <Input name="time_zone" defaultValue={editingItem?.time_zone} placeholder="e.g., Asia/Kolkata" />
            </div>
          </>
        );

      case 'storage-locations':
        return (
          <>
            <div>
              <Label>Storage Location ID</Label>
              <Input 
                name="storage_location_id"
                defaultValue={editingItem?.storage_location_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., FG01, 0001"
                maxLength={10}
              />
            </div>
            <div>
              <Label>Plant</Label>
              <Select value={selectedPlant} onValueChange={setSelectedPlant} required>
                <SelectTrigger><SelectValue placeholder="Select Plant" /></SelectTrigger>
                <SelectContent>
                  {data.plants.map(p => (
                    <SelectItem key={p.plant_id} value={p.plant_id}>
                      {p.plant_id} - {p.plant_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="plant_id" value={selectedPlant} />
            </div>
            <div>
              <Label>Name</Label>
              <Input 
                name="name"
                defaultValue={editingItem?.name}
                required
                placeholder="e.g., Finished Goods 1"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input 
                name="description"
                defaultValue={editingItem?.description}
                placeholder="Optional longer description"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
            <div>
              <Label>Type (optional)</Label>
              <Select value={selectedType || undefined} onValueChange={setSelectedType}>
                <SelectTrigger><SelectValue placeholder="Select Type (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Raw Material">Raw Material</SelectItem>
                  <SelectItem value="Finished Goods">Finished Goods</SelectItem>
                  <SelectItem value="Quarantine">Quarantine</SelectItem>
                  <SelectItem value="Staging">Staging</SelectItem>
                  <SelectItem value="Consignment">Consignment</SelectItem>
                  <SelectItem value="Scrap">Scrap</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="type" value={selectedType || ''} />
            </div>
            <div>
              <Label>Address (optional)</Label>
              <Input 
                name="address"
                defaultValue={editingItem?.address}
                placeholder="e.g., Block B, 2nd Lane"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Capacity (optional)</Label>
                <Input 
                  name="capacity"
                  type="number"
                  defaultValue={editingItem?.capacity}
                  placeholder="e.g., 20000"
                  min="0"
                />
              </div>
              <div>
                <Label>Zone (optional)</Label>
                <Input 
                  name="zone"
                  defaultValue={editingItem?.zone}
                  placeholder="e.g., A1"
                />
              </div>
            </div>
            <div>
              <Label>Manager ID (optional)</Label>
              <Input 
                name="manager_id"
                defaultValue={editingItem?.manager_id}
                placeholder="e.g., EMP50123"
              />
            </div>
          </>
        );

      case 'purchasing-organizations':
        return (
          <>
            <div>
              <Label>Purchasing Organization ID</Label>
              <Input name="purchasing_org_id" defaultValue={editingItem?.purchasing_org_id} required disabled={!!editingItem} placeholder="e.g., P100" maxLength={10} />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required placeholder="e.g., India Main Procurement" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input name="description" defaultValue={editingItem?.description} placeholder="Long text or details" />
            </div>
            <div>
              <Label>Company Code (optional)</Label>
              <Select value={selectedCompanyCode || undefined} onValueChange={setSelectedCompanyCode}>
                <SelectTrigger><SelectValue placeholder="Select Company Code (optional)" /></SelectTrigger>
                <SelectContent>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="company_code_id" value={selectedCompanyCode || ''} />
            </div>
            <div>
              <Label>Address (optional)</Label>
              <Input name="address" defaultValue={editingItem?.address} placeholder="Office location" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Language (optional)</Label>
                <Select value={selectedLanguage || undefined} onValueChange={setSelectedLanguage}>
                  <SelectTrigger><SelectValue placeholder="Select Language" /></SelectTrigger>
                  <SelectContent>
                    {languages.map(l => (
                      <SelectItem key={l.language_code} value={l.language_code}>
                        {l.language_code} - {l.language_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" name="language" value={selectedLanguage || ''} />
              </div>
              <div>
                <Label>Currency (optional)</Label>
                <Select value={selectedCurrency || undefined} onValueChange={setSelectedCurrency}>
                  <SelectTrigger><SelectValue placeholder="Select Currency" /></SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => (
                      <SelectItem key={c.currency_code} value={c.currency_code}>
                        {c.currency_code} - {c.currency_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" name="currency" value={selectedCurrency || ''} />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
          </>
        );

      case 'purchasing-groups':
        return (
          <>
            <div>
              <Label>Purchasing Group ID</Label>
              <Input name="purchasing_group_id" defaultValue={editingItem?.purchasing_group_id} required disabled={!!editingItem} placeholder="e.g., 001" maxLength={10} />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required placeholder="e.g., Raw Materials" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input name="description" defaultValue={editingItem?.description} placeholder="Department/commodity/region" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email (optional)</Label>
                <Input name="email" type="email" defaultValue={editingItem?.email} placeholder="contact@example.com" />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input name="phone" defaultValue={editingItem?.phone} placeholder="+1234567890" />
              </div>
            </div>
            <div>
              <Label>Manager ID (optional)</Label>
              <Input name="manager_id" defaultValue={editingItem?.manager_id} placeholder="e.g., EMP501" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const getTableColumns = () => {
    const columnsMap = {
      'holiday-calendars': ['ID', 'Description', 'Country', 'Region', 'Valid From', 'Valid To', 'Holidays', 'Actions'],
      'factory-calendars': ['ID', 'Description', 'Holiday Calendar', 'Valid From', 'Valid To', 'Workdays', 'Actions'],
      'plants': ['ID', 'Name', 'Company Code', 'City', 'Country', 'Currency', 'Factory Calendar', 'Actions'],
      'storage-locations': ['ID', 'Plant', 'Name', 'Status', 'Type', 'Zone', 'Capacity', 'Actions'],
      'purchasing-organizations': ['ID', 'Name', 'Company Code', 'Address', 'Language', 'Currency', 'Status', 'Actions'],
      'purchasing-groups': ['ID', 'Name', 'Email', 'Phone', 'Manager', 'Status', 'Actions'],
    };
    return columnsMap[activeTab] || [];
  };

  const getTableRow = (item) => {
    switch (activeTab) {
      case 'holiday-calendars':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.holiday_calendar_id}</td>
            <td className="py-3 px-4 text-sm">{item.description}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.country}</td>
            <td className="py-3 px-4 text-xs">{item.region || '-'}</td>
            <td className="py-3 px-4 text-xs">{item.valid_from}</td>
            <td className="py-3 px-4 text-xs">{item.valid_to}</td>
            <td className="py-3 px-4 text-xs">
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                {item.holidays?.length || 0} holidays
              </span>
            </td>
          </>
        );
      case 'factory-calendars':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.factory_calendar_id}</td>
            <td className="py-3 px-4 text-sm">{item.description}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.holiday_calendar_id}</td>
            <td className="py-3 px-4 text-xs">{item.valid_from}</td>
            <td className="py-3 px-4 text-xs">{item.valid_to}</td>
            <td className="py-3 px-4 text-xs">
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                {item.workdays?.length || 0} days
              </span>
            </td>
          </>
        );
      case 'plants':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.plant_id}</td>
            <td className="py-3 px-4 text-sm">{item.plant_name}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.company_code_id}</td>
            <td className="py-3 px-4 text-sm">{item.city}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.country}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.currency}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.factory_calendar_id}</td>
          </>
        );
      case 'storage-locations':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.storage_location_id}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.plant_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 text-xs">
              <span className={`px-2 py-1 rounded ${
                item.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {item.status}
              </span>
            </td>
            <td className="py-3 px-4 text-xs">{item.type || '-'}</td>
            <td className="py-3 px-4 text-xs">{item.zone || '-'}</td>
            <td className="py-3 px-4 text-xs">{item.capacity ? item.capacity.toLocaleString() : '-'}</td>
          </>
        );
      case 'purchasing-organizations':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.purchasing_org_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.company_code_id || '-'}</td>
            <td className="py-3 px-4 text-xs">{item.address || '-'}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.language || '-'}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.currency || '-'}</td>
            <td className="py-3 px-4 text-xs">
              <span className={`px-2 py-1 rounded ${item.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {item.status}
              </span>
            </td>
          </>
        );
      case 'purchasing-groups':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.purchasing_group_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 text-xs">{item.email || '-'}</td>
            <td className="py-3 px-4 text-xs">{item.phone || '-'}</td>
            <td className="py-3 px-4 text-xs">{item.manager_id || '-'}</td>
            <td className="py-3 px-4 text-xs">
              <span className={`px-2 py-1 rounded ${item.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {item.status}
              </span>
            </td>
          </>
        );
      default:
        return null;
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'holiday-calendars':
        return data.holidayCalendars;
      case 'factory-calendars':
        return data.factoryCalendars;
      case 'plants':
        return data.plants;
      case 'storage-locations':
        return data.storageLocations;
      case 'purchasing-organizations':
        return data.purchasingOrgs;
      case 'purchasing-groups':
        return data.purchasingGroups;
      default:
        return [];
    }
  };

  const tabs = [
    { id: 'holiday-calendars', label: 'Holiday Calendars' },
    { id: 'factory-calendars', label: 'Factory Calendars' },
    { id: 'plants', label: 'Plants' },
    { id: 'storage-locations', label: 'Storage Locations' },
    { id: 'purchasing-organizations', label: 'Purchasing Organizations' },
    { id: 'purchasing-groups', label: 'Purchasing Groups' },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Material Management (MM)</h2>
          <p className="text-sm text-gray-500 mt-1">Manage plants, factory calendars, and holiday calendars</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  setEditingItem(null);
                  setIsDialogOpen(false);
                  setHolidays([]);
                  setSpecialRules([]);
                  setSelectedWorkdays([]);
                  setSelectedType('');
                  setSelectedPlant('');
                  setSelectedStatus('Active');
                  setSelectedCountry('');
                  setSelectedCurrency('');
                  setSelectedLanguage('');
                  setSelectedCompanyCode('');
                  setSelectedFactoryCalendar('');
                  setSelectedHolidayCalendar('');
                }}
                className={`px-6 py-4 font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {tabs.find(t => t.id === activeTab)?.label}
            </h3>
            <Dialog 
              key={`${activeTab}-${editingItem ? 'edit' : 'new'}`}
              open={isDialogOpen} 
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) {
                  setEditingItem(null);
                  setHolidays([]);
                  setSpecialRules([]);
                  setSelectedWorkdays([]);
                  setSelectedPlant('');
                  setSelectedStatus('Active');
                  setSelectedType('');
                  setSelectedCountry('');
                  setSelectedCurrency('');
                  setSelectedLanguage('');
                  setSelectedCompanyCode('');
                  setSelectedFactoryCalendar('');
                  setSelectedHolidayCalendar('');
                  setFormResetCounter(prev => prev + 1); // Force form remount
                }
              }}>
              <DialogTrigger asChild>
                <Button type="button" data-testid="btn-add-new" disabled={!canWrite(activeTab)} onClick={() => {
                  setEditingItem(null);
                  setHolidays([]);
                  setSpecialRules([]);
                  setSelectedWorkdays([]);
                  setSelectedCountry('');
                  setSelectedCurrency('');
                  setSelectedLanguage('');
                  setSelectedCompanyCode('');
                  setSelectedFactoryCalendar('');
                  setSelectedHolidayCalendar('');
                  setSelectedPlant('');
                  setSelectedStatus('Active');
                  setSelectedType('');
                  setFormResetCounter(prev => prev + 1); // Force form remount
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add New
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto max-w-3xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? 'Edit' : 'Create'} {tabs.find(t => t.id === activeTab)?.label}
                  </DialogTitle>
                </DialogHeader>
                <form 
                  key={`form-${activeTab}-${formResetCounter}`}
                  onSubmit={handleSubmit} 
                  className="space-y-4"
                >
                  {getFormFields()}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsDialogOpen(false);
                        setEditingItem(null);
                        setHolidays([]);
                        setSpecialRules([]);
                        setSelectedWorkdays([]);
                        setSelectedType('');
                        setSelectedPlant('');
                        setSelectedStatus('Active');
                        setSelectedCountry('');
                        setSelectedCurrency('');
                        setSelectedLanguage('');
                        setSelectedCompanyCode('');
                        setSelectedFactoryCalendar('');
                        setSelectedHolidayCalendar('');
                        setFormResetCounter(prev => prev + 1); // Force form remount
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {getTableColumns().map((col, idx) => (
                    <th key={idx} className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getCurrentData().length === 0 ? (
                  <tr>
                    <td colSpan={getTableColumns().length} className="text-center py-8 text-gray-500">
                      No records found
                    </td>
                  </tr>
                ) : (
                  getCurrentData().map((item, index) => {
                    // Generate unique key based on active tab
                    let itemKey;
                    switch(activeTab) {
                      case 'holiday-calendars':
                        itemKey = item.holiday_calendar_id;
                        break;
                      case 'factory-calendars':
                        itemKey = item.factory_calendar_id;
                        break;
                      case 'plants':
                        itemKey = item.plant_id;
                        break;
                      case 'storage-locations':
                        itemKey = `${item.plant_id}-${item.storage_location_id}`;
                        break;
                      case 'purchasing-organizations':
                        itemKey = item.purchasing_org_id;
                        break;
                      case 'purchasing-groups':
                        itemKey = item.purchasing_group_id;
                        break;
                      default:
                        itemKey = index;
                    }
                    return (
                    <tr key={`${activeTab}-${itemKey}`} className="border-b border-gray-100 hover:bg-gray-50">
                      {getTableRow(item)}
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <RecordActions
                            doctype={activeTab}
                            idForTestId={itemKey}
                            onView={() => setViewItem(item)}
                            onEdit={() => handleEdit(item)}
                          />
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title={`${tabs.find(t => t.id === activeTab)?.label || 'Record'} Details`}
        record={viewItem}
      />

    </Layout>
  );
};

export default MaterialManagement;
